import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../../components/sp-spinner-overlay/sp-spinner-overlay';
import '../../components/sp-image/sp-image';

import {customElement, property, state} from 'lit/decorators.js';
import {css, html, LitElement, PropertyValues} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {translate} from '@stefanholzapfel/lit-translate';
import {headers} from '../../styles/headers';
import {SpPhotoswipeController} from "../../controllers/sp-photoswipe.controller";
import {when} from "lit/directives/when.js";
import {SpUiTaxon} from "../inputs/sp-taxon-input/sp-taxon-input";
import {SpUiConfigService} from "../../services/sp-ui-config.service";
import {unsafeCSSIcon} from "../../styles/icons";

@customElement('sp-taxon-info')
export class SpTaxonInfo extends LitElement {
    @property({type: Object})
    taxon: SpUiTaxon;

    @state()
    info: SpTaxonInfoEntity;
    @state()
    infoLoading = false;
    infoAbortController = new AbortController();

    photoswipeController: SpPhotoswipeController;

    static styles = [
        headers,
        //language=CSS
        css`
            h1 {
                color: rgb(var(--sp-color-a));
            }

            h2 {
                color: rgb(var(--sp-color-a));
            }

            sl-spinner {
                font-size: 2rem;
            }

            .optinfo {
                position: relative;
                height: 100%;
                background-color: rgb(var(--sp-greylight));
            }
            
            .wiki {
                box-sizing:border-box;
            }

            a {
                text-decoration: none;
                color: rgb(var(--sp-color-a));
            }

            .image {
                position: relative;
                aspect-ratio: 4 / 3;
                border-radius: var(--sp-default-border-radius);
                cursor: pointer;
            }

            /* This is a best effort approach for legacy browsers where heights are hardcoded */
            @supports not (aspect-ratio: 1) {
                .image {
                    height: 360px;
                }
            }
            
            .title:first-child h1 {
                margin-top: 0;
            }

            .zoom {
                position: absolute;
                right: 10px;
                bottom: 10px;
                width: 32px;
                height: 32px;
                background-size: auto 100%;
                background-position: center center;
                background-image: url(${unsafeCSSIcon('search', { size: 'x128' })});
                opacity: 75%;
            }`
    ]

    constructor() {
        super();
        this.photoswipeController = new SpPhotoswipeController(this, {galleryId: 'taxon'});
    }

    protected willUpdate(props: PropertyValues) {
        super.willUpdate(props);
        if (props.has('taxon') && this.taxon) this.loadInfo();
        else if (props.has('taxon') && !this.taxon) this.resetInfo();
    }

    render() {
        return when(this.info,
            () =>
                html`
                    <div class="optinfo">
                        <div class="wiki">
                            ${ when(this.info.image, () =>
                                    html`
                                        <sp-image 
                                             class="image"
                                             .image=${`${this.info.image}?width=800px`}
                                             @click=${() => {
                                                 this.openImage(`${this.info.image}?width=800px`)
                                             }}>
                                            <span class="zoom"></span></sp-image>
                                    `)}
                            <div class="title"><h1>${this.info.title}</h1></div>
                            <div>${unsafeHTML(this.info.text)}</div>
                            <a href=${this.info.link}
                               target="_blank">${translate('TAXON_FIELD_WIKIPEDIA_READ_ON')}</a>
                            <br/><br/>
                        </div>
                    </div>`,
            () =>
            html`<sp-spinner-overlay></sp-spinner-overlay>`)
    }

    async openImage(src: string) {
        await this.photoswipeController.updateData([{
            fullSize: src,
            thumb: src
        }]);
        this.photoswipeController.open();
    }

    loadInfo() {
        if ((this.taxon as any).wikipedia) {
            this.infoLoading = true;
            this.infoAbortController.abort();
            this.infoAbortController = new AbortController();
            this.fetchTaxonInfo(this.taxon, this.infoAbortController).then(taxonInfo => {
                this.infoLoading = false;
                this.info = taxonInfo;
            }).catch(e => {
                this.infoLoading = false;
                this.info = null;
            });
        }
    }

    resetInfo() {
        if (this.infoAbortController) this.infoAbortController.abort();
        this.infoLoading = false;
        this.info = null;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.infoAbortController) this.infoAbortController.abort();
    }

    async fetchTaxonInfo(
        taxon: SpUiTaxon,
        abortController?: AbortController
    ): Promise<SpTaxonInfoEntity> {
        const url = new URL(`https://${ taxon.lang }.wikipedia.org/w/api.php`);
        url.searchParams.append('action', 'query');
        url.searchParams.append('prop', 'extracts');
        url.searchParams.append('exsentences', '10');
        url.searchParams.append('titles', taxon.wikipedia);
        url.searchParams.append('format', 'json');
        url.searchParams.append('origin', '*');
        return new Promise((resolve, reject) => {
            fetch(
                url.href,
                {
                    method: 'GET',
                    signal: abortController ? abortController.signal : null
                }
            )
                .then(result => result.json())
                .then(result => {
                    const data = result.query?.pages[Object.keys(result.query.pages)[0]];
                    resolve({
                        ...taxon.image && { image: `${ SpUiConfigService.get('SP_WIKIMEDIA_URI') }/` + encodeURI(taxon.image).replace(/'/g, "%27") },
                        title: data.title,
                        text: data.extract,
                        link: 'https://' + taxon.lang + '.wikipedia.org/wiki/' + data.title
                    });
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
}

export interface SpTaxonInfoEntity {
    image: string;
    title: string;
    text: string;
    link: string;
}